import React from 'react';
import { Activity, BookOpen, CheckCircle2, Clock3, TrendingUp, Users } from 'lucide-react';

import { Card, CardContent } from 'components/ui/card';

interface OrganizationSummaryProps {
  activeUserCount: number;
  defaultCourseCount: number;
  averageProgressPercent: number;
  usersInProgressCount: number;
  usersFullyCompleteCount: number;
  usersActiveLast7DaysCount: number;
}

const SummaryTile: React.FC<{
  icon: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
    <CardContent variant="custom" padding="none" className="flex items-center gap-4 p-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-sm leading-tight text-muted-foreground">{label}</div>
        <div className="truncate font-medium text-foreground">{value}</div>
      </div>
    </CardContent>
  </Card>
);

export const OrganizationSummary: React.FC<OrganizationSummaryProps> = ({
  activeUserCount,
  defaultCourseCount,
  averageProgressPercent,
  usersInProgressCount,
  usersFullyCompleteCount,
  usersActiveLast7DaysCount,
}) => {
  return (
    <section aria-labelledby="organization-summary-heading" className="space-y-4">
      <div className="space-y-1">
        <h2 id="organization-summary-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
          Organization Summary
        </h2>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <SummaryTile
          icon={<Users className="size-5" />}
          label={
            <>
              <span className="block">Active</span>
              <span className="block">users</span>
            </>
          }
          value={<span className="text-2xl font-semibold">{activeUserCount}</span>}
        />
        <SummaryTile
          icon={<BookOpen className="size-5" />}
          label={
            <>
              <span className="block">Org default</span>
              <span className="block">courses</span>
            </>
          }
          value={<span className="text-2xl font-semibold">{defaultCourseCount}</span>}
        />
        <SummaryTile
          icon={<TrendingUp className="size-5" />}
          label={
            <>
              <span className="block">Average org</span>
              <span className="block">progress</span>
            </>
          }
          value={<span className="text-2xl font-semibold">{Math.round(averageProgressPercent)}%</span>}
        />
        <SummaryTile
          icon={<Clock3 className="size-5" />}
          label={
            <>
              <span className="block">Users in</span>
              <span className="block">progress</span>
            </>
          }
          value={<span className="text-2xl font-semibold">{usersInProgressCount}</span>}
        />
        <SummaryTile
          icon={<CheckCircle2 className="size-5" />}
          label={
            <>
              <span className="block">Users fully</span>
              <span className="block">complete</span>
            </>
          }
          value={<span className="text-2xl font-semibold">{usersFullyCompleteCount}</span>}
        />
        <SummaryTile
          icon={<Activity className="size-5" />}
          label={
            <>
              <span className="block">Active in</span>
              <span className="block">last 7 days</span>
            </>
          }
          value={<span className="text-2xl font-semibold">{usersActiveLast7DaysCount}</span>}
        />
      </div>
    </section>
  );
};
